import {
  HistoryFromField,
  defaultHistoryFrom,
  historyFromPayload,
  historyNote,
} from "@/components/analysis/history-from-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { ApiError, api } from "@/lib/api";
import { Link, Navigate, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

/**
 * `/agents/analyst/discord` — admin-only. The list of watched Discord channels; each
 * opens its full transcript. Top-right: "Watch new channel" (modal picker of
 * every text channel the bot can see) and "Bot" (modal with the bot's status
 * and setup instructions).
 *
 * E2E behaviour contract lives in `e2e/analysis-discord.spec.ts`.
 */

type Project = { id: string; name: string };
type PickerChannel = {
  channelId: string;
  name: string;
  guildName: string;
  enabled: boolean;
  projectId: string | null;
};
type WatchedChannel = {
  channelId: string;
  name: string;
  guildName: string | null;
  projectId: string | null;
  projectName: string | null;
  historyFrom: string | null;
  messageCount: number;
  processedCount: number;
  /** Messages kept only as background for the AI (older than historyFrom). */
  contextCount: number;
};
type DiscordStatus = {
  enabled: boolean;
  connected: boolean;
  bot: { id: string; name: string } | null;
  guilds: Array<{ id: string; name: string }>;
  error: string | null;
};

export function DiscordPage() {
  const { t } = useTranslation();
  const { user } = useLoaderData({ from: "/_app" });
  const [projects, setProjects] = useState<Project[]>([]);
  const [status, setStatus] = useState<DiscordStatus | null>(null);
  const [watched, setWatched] = useState<WatchedChannel[]>([]);
  const [picker, setPicker] = useState<PickerChannel[]>([]);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [historyFrom, setHistoryFrom] = useState(defaultHistoryFrom());

  const isAdmin = user.globalRole === "admin";

  const loadWatched = useCallback(
    () =>
      api
        .get<{ channels: WatchedChannel[] }>("/api/auto-sources/watched-discord-channels")
        .then((r) => setWatched(r.channels))
        .catch(() => {}),
    [],
  );
  const loadPicker = useCallback(
    () =>
      api
        .get<{ channels: PickerChannel[] }>("/api/auto-sources/discord-channels")
        .then((r) => {
          setPicker(r.channels);
          setPickerError(null);
        })
        .catch(() => setPickerError(t("agentTabs.discord.pickerError"))),
    [t],
  );

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get<{ projects: Project[] }>("/api/projects?pageSize=200")
      .then((r) => setProjects(r.projects.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {});
    api
      .get<DiscordStatus>("/api/analysis/discord/status")
      .then(setStatus)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 503) {
          setStatus({ enabled: false, connected: false, bot: null, guilds: [], error: null });
        }
      });
    void loadWatched();
  }, [isAdmin, loadWatched]);

  if (!isAdmin) return <Navigate to="/" />;

  async function watchChannel(ch: PickerChannel) {
    await api
      .post("/api/auto-sources/watched-discord-channels", {
        channelId: ch.channelId,
        name: ch.name,
        guildName: ch.guildName,
        historyFrom: historyFromPayload(historyFrom),
      })
      .catch(() => {});
    await loadWatched();
    await loadPicker();
  }
  async function unwatchChannel(channelId: string) {
    await api
      .delete(`/api/auto-sources/watched-discord-channels/${encodeURIComponent(channelId)}`)
      .catch(() => {});
    await loadWatched();
  }
  async function setChannelProject(channelId: string, projectId: string | null) {
    setWatched((prev) =>
      prev.map((c) =>
        c.channelId === channelId
          ? { ...c, projectId, projectName: projects.find((p) => p.id === projectId)?.name ?? null }
          : c,
      ),
    );
    await api
      .patch(`/api/auto-sources/watched-discord-channels/${encodeURIComponent(channelId)}`, {
        projectId,
      })
      .catch(() => void loadWatched());
  }

  const projectOptions = projects.map((p) => ({ value: p.id, label: p.name }));
  const watchedIds = new Set(watched.map((c) => c.channelId));
  const pickable = picker.filter((c) => !c.enabled && !watchedIds.has(c.channelId));
  const connected = status?.connected === true;

  return (
    <div className="w-full space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">{t("agentTabs.discord.title")}</h2>
          <p className="text-muted-foreground mt-1">{t("agentTabs.discord.intro")}</p>
        </div>
        <div className="flex gap-2">
          {connected && (
            <Dialog onOpenChange={(o) => o && loadPicker()}>
              <DialogTrigger asChild>
                <Button type="button" size="sm" data-testid="watch-new-open">
                  {t("agentTabs.discord.watchNew")}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{t("agentTabs.discord.watchTitle")}</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">{t("agentTabs.discord.watchHint")}</p>
                <HistoryFromField value={historyFrom} onChange={setHistoryFrom} />
                {pickerError && <p className="text-sm text-destructive">{pickerError}</p>}
                {!pickerError && pickable.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t("agentTabs.discord.nothingToWatch")}
                  </p>
                ) : (
                  <ul className="max-h-80 divide-y overflow-auto rounded-md border">
                    {pickable.map((ch) => (
                      <li
                        key={ch.channelId}
                        className="flex items-center justify-between gap-3 px-3 py-2"
                        data-testid="watch-candidate"
                      >
                        <span className="text-sm">
                          <span className="font-medium">#{ch.name}</span>
                          <span className="text-xs text-muted-foreground"> · {ch.guildName}</span>
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => watchChannel(ch)}
                        >
                          {t("agentTabs.discord.watch")}
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </DialogContent>
            </Dialog>
          )}
          <DiscordBotModal status={status} />
        </div>
      </div>

      {!connected ? (
        <p className="text-sm text-muted-foreground" data-testid="discord-not-connected">
          <Trans i18nKey="agentTabs.discord.notConnected" components={{ b: <strong /> }} />
        </p>
      ) : watched.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="watched-empty">
          <Trans i18nKey="agentTabs.discord.watchedEmpty" components={{ b: <strong /> }} />
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {watched.map((ch) => (
            <li
              key={ch.channelId}
              className="flex flex-wrap items-center justify-between gap-3 px-3 py-2"
              data-testid="watched-channel-row"
            >
              <div className="min-w-0 flex-1">
                <Link
                  to="/analysis/discord/$channelId"
                  params={{ channelId: ch.channelId }}
                  className="font-medium underline"
                  data-testid="watched-channel-open"
                >
                  #{ch.name}
                </Link>
                {ch.guildName && (
                  <span className="ml-1 text-xs text-muted-foreground">· {ch.guildName}</span>
                )}
                <span className="ml-2 text-xs text-muted-foreground">
                  {t("agentTabs.discord.processed", {
                    done: ch.processedCount,
                    total: ch.messageCount,
                  })}
                </span>
                <span
                  className="ml-2 text-xs text-muted-foreground"
                  data-testid="history-from-note"
                >
                  {historyNote(ch.historyFrom, ch.contextCount)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Select
                  className="w-44"
                  value={ch.projectId ?? ""}
                  onChange={(v) => setChannelProject(ch.channelId, v || null)}
                  aria-label={t("agentTabs.discord.projectFor", { name: ch.name })}
                  options={[
                    { value: "", label: t("agentTabs.discord.noProject") },
                    ...projectOptions,
                  ]}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => unwatchChannel(ch.channelId)}
                  data-testid="unwatch-channel"
                >
                  {t("agentTabs.discord.unwatch")}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Bot status + setup instructions, in a modal opened by the "Bot" button. */
function DiscordBotModal({ status }: { status: DiscordStatus | null }) {
  const { t } = useTranslation();
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" data-testid="discord-bot-open">
          {t("agentTabs.discord.bot")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("agentTabs.discord.botTitle")}</DialogTitle>
        </DialogHeader>
        {status === null ? (
          <p className="text-sm text-muted-foreground">{t("agentTabs.common.loading")}</p>
        ) : !status.enabled ? (
          <div className="space-y-2 text-sm text-muted-foreground" data-testid="discord-off">
            <p>
              <Trans i18nKey="agentTabs.discord.off" components={{ c: <code /> }} />
            </p>
            <ol className="list-decimal space-y-1 pl-5">
              <li>
                <Trans i18nKey="agentTabs.discord.step1" components={{ c: <code /> }} />
              </li>
              <li>
                <Trans i18nKey="agentTabs.discord.step2" components={{ b: <strong /> }} />
              </li>
              <li>
                <Trans i18nKey="agentTabs.discord.step3" components={{ c: <code /> }} />
              </li>
            </ol>
          </div>
        ) : status.connected ? (
          <div className="space-y-3" data-testid="discord-connected">
            <p className="text-sm">
              <Trans
                i18nKey="agentTabs.discord.connectedAs"
                values={{ name: status.bot?.name ?? "—" }}
                components={{ b: <span className="font-medium" /> }}
              />
            </p>
            <div className="text-sm text-muted-foreground">
              {status.guilds.length === 0 ? (
                <p>
                  <Trans i18nKey="agentTabs.discord.noGuilds" components={{ c: <code /> }} />
                </p>
              ) : (
                <p>
                  {t("agentTabs.discord.servers", {
                    list: status.guilds.map((g) => g.name).join(", "),
                  })}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm" data-testid="discord-error">
            <p className="text-muted-foreground">{t("agentTabs.discord.tokenBroken")}</p>
            {status.error && <p className="text-xs text-destructive">{status.error}</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
